import React, { useMemo, useState } from 'react';
import { Chip, IconButton, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { DataTable } from '../common/DataTable';
import { ModuleColumn } from '../../types';
import { RADIUS, brandColors, BASE_FONT_SIZE, brandCss } from '../../theme/appTheme';
import { DEFAULT_ROWS_PER_PAGE, ROWS_PER_PAGE_OPTIONS } from '../../constants/tableDefaults';

export interface AdminColumn<T> {
  id: string;
  header: string;
  render: (row: T) => React.ReactNode;
  searchValue?: (row: T) => string;
  exportValue?: (row: T) => unknown;
  sortValue?: (row: T) => unknown;
  sortable?: boolean;
  filterable?: boolean;
  filterType?: 'text' | 'select';
  filterOptions?: StatusFilterOption[];
  filterValue?: (row: T) => string;
}

export interface StatusFilterOption {
  label: string;
  value: string;
}

export interface ExportExtraColumn<T> {
  id: string;
  header: string;
  exportValue?: (row: T) => unknown;
  searchValue?: (row: T) => string;
}

interface SuperAdminDataTableProps<T> {
  columns: AdminColumn<T>[];
  rows: T[];
  loading?: boolean;
  error?: string;
  searchPlaceholder?: string;
  statusField?: string;
  statusOptions?: StatusFilterOption[];
  getRowId?: (row: T) => string;
  getStatus?: (row: T) => string;
  renderActions?: (row: T) => React.ReactNode;
  emptyMessage?: string;
  recordLabel?: string;
  defaultRowsPerPage?: number;
  rowsPerPageOptions?: readonly number[];
  exportFileName?: string;
  exportSheetName?: string;
  enableExport?: boolean;
  enableImport?: boolean;
  onImport?: (records: Record<string, unknown>[]) => void;
  canExport?: boolean;
  canImport?: boolean;
  enableColumnFilters?: boolean;
  defaultSortColumnId?: string;
  defaultSortDirection?: 'asc' | 'desc';
  selectable?: boolean;
  onSelectionChange?: (ids: string[]) => void;
  onRowClick?: (row: T) => void;
  renderExpandedRow?: (row: T) => React.ReactNode;
  exportExtraColumns?: ExportExtraColumn<T>[];
}

function toModuleColumns<T>(columns: AdminColumn<T>[]): ModuleColumn[] {
  return columns.map((col) => ({
    field: col.id,
    header: col.header,
    sortable: col.sortable,
    render: (row) => col.render(row as T),
    searchValue: col.searchValue ? (row) => col.searchValue!(row as T) : undefined,
    exportValue: col.exportValue ? (row) => col.exportValue!(row as T) : undefined,
    sortValue: col.sortValue ? (row) => col.sortValue!(row as T) : undefined,
    filterable: col.filterable,
    filterType: col.filterType,
    filterOptions: col.filterOptions,
    filterValue: col.filterValue ? (row) => col.filterValue!(row as T) : undefined,
  }));
}

export function SuperAdminDataTable<T extends Record<string, unknown>>({
  columns,
  rows,
  loading,
  error,
  searchPlaceholder = 'Search...',
  statusField,
  statusOptions = [
    { label: 'All Status', value: 'all' },
    { label: 'Active', value: 'active' },
    { label: 'Pending', value: 'pending' },
    { label: 'Inactive', value: 'inactive' },
    { label: 'Suspended', value: 'suspended' },
  ],
  getRowId = (row) => String(row._id ?? row.id ?? Math.random()),
  getStatus = (row) => String(statusField ? row[statusField] : row.status ?? 'active'),
  renderActions,
  emptyMessage = 'No records found',
  recordLabel = 'records',
  defaultRowsPerPage = DEFAULT_ROWS_PER_PAGE,
  rowsPerPageOptions = ROWS_PER_PAGE_OPTIONS,
  exportFileName = 'super-admin-export',
  exportSheetName = 'Data',
  enableExport = true,
  enableImport = false,
  onImport,
  canExport = true,
  canImport,
  enableColumnFilters = true,
  selectable = true,
  onSelectionChange,
  onRowClick,
  renderExpandedRow,
  exportExtraColumns = [],
}: SuperAdminDataTableProps<T>) {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);

  const moduleColumns = useMemo(() => toModuleColumns(columns), [columns]);
  const tableRows = rows as Record<string, unknown>[];
  const showStatusFilter = Boolean(statusField) || statusOptions.length > 1;

  return (
    <DataTable
      columns={moduleColumns}
      rows={tableRows}
      loading={loading}
      error={error}
      search={search}
      onSearchChange={(v) => { setSearch(v); setPage(0); }}
      page={page}
      rowsPerPage={rowsPerPage}
      onPageChange={setPage}
      onRowsPerPageChange={(n) => { setRowsPerPage(n); setPage(0); }}
      rowsPerPageOptions={rowsPerPageOptions}
      recordLabel={recordLabel}
      searchPlaceholder={searchPlaceholder}
      emptyMessage={emptyMessage}
      getRowId={(row) => getRowId(row as T)}
      getStatus={(row) => getStatus(row as T)}
      statusOptions={statusOptions}
      forceStatusFilter={showStatusFilter}
      renderActions={renderActions ? (row) => renderActions(row as T) : undefined}
      enableColumnFilters={enableColumnFilters}
      enableExcelExport={enableExport}
      canExport={canExport}
      canImport={canImport ?? enableImport}
      onImport={onImport}
      exportFileName={exportFileName}
      exportSheetName={exportSheetName}
      selectable={selectable}
      onSelectionChange={onSelectionChange}
      onRowClick={onRowClick ? (row) => onRowClick(row as T) : undefined}
      renderExpandedRow={renderExpandedRow ? (row) => renderExpandedRow(row as T) : undefined}
      exportExtraColumns={exportExtraColumns.map((col) => ({
        id: col.id,
        header: col.header,
        exportValue: col.exportValue ? (row) => col.exportValue!(row as T) : undefined,
        searchValue: col.searchValue ? (row) => col.searchValue!(row as T) : undefined,
      }))}
    />
  );
}

export const AdminTableEditButton: React.FC<{ onClick: () => void; title?: string }> = ({ onClick, title = 'Edit' }) => (
  <Tooltip title={title}>
    <IconButton size="small" onClick={(e) => { e.stopPropagation(); onClick(); }} color="primary">
      <EditIcon fontSize="small" />
    </IconButton>
  </Tooltip>
);

export const statusChip = (status: string) => {
  const map: Record<string, { bg: string; color: string }> = {
    active: { bg: brandCss.green100, color: brandCss.primaryDark },
    pending: { bg: '#fef3c7', color: '#d97706' },
    suspended: { bg: '#fee2e2', color: '#dc2626' },
    inactive: { bg: '#fee2e2', color: '#dc2626' },
    rejected: { bg: '#f1f5f9', color: brandColors.textMuted },
    paid: { bg: brandCss.green100, color: brandCss.primaryDark },
    partially_paid: { bg: '#fef3c7', color: '#d97706' },
    overdue: { bg: '#fee2e2', color: '#dc2626' },
    partial: { bg: '#fef3c7', color: '#d97706' },
    failed: { bg: '#fee2e2', color: '#dc2626' },
    refunded: { bg: '#f1f5f9', color: brandColors.textMuted },
  };
  const c = map[String(status).toLowerCase()] || map.active;
  return (
    <Chip
      label={String(status).replace('_', ' ')}
      size="small"
      sx={{ bgcolor: c.bg, color: c.color, fontWeight: 700, fontSize: BASE_FONT_SIZE, height: 22, textTransform: 'capitalize', borderRadius: `${RADIUS}px` }}
    />
  );
};

export const apiErrorMessage = (err: unknown, fallback = 'Request failed') => {
  const status = (err as { response?: { status?: number; data?: { message?: string } } })?.response?.status;
  const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
  if (status === 404) return `${msg || 'API not found'}. Restart server: cd server && npm start`;
  if (status === 403) return msg || 'Access denied';
  return msg || fallback;
};

export const STATUS_FILTER_OPTIONS: StatusFilterOption[] = [
  { label: 'Active', value: 'active' },
  { label: 'Inactive', value: 'inactive' },
  { label: 'Pending', value: 'pending' },
  { label: 'Suspended', value: 'suspended' },
];
